import { useMemo } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../../utils/datetime';
import type { SuboutLookupItem, SuboutTestPriceRow } from './types';

// Subcontract Test Order → "Test Price" tab (G5 SubOutSampleRegistrations_TestPrices_ListView,
// InlineEditMode=Batch, no New / Delete / Link). Columns and captions in G5 order; TAT,
// UnitPrice and Comment are editable. Every edit goes to the server, which recomputes the
// row (SubTotal, %Change, the matched master price) — this component only displays it.

const money = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  rows: SuboutTestPriceRow[];
  total: number;
  turnAroundTimes: SuboutLookupItem[];
  readOnly: boolean;
  loading: boolean;
  /** A cell was edited: 'tat' carries the TAT Oid, 'price' the number, 'comment' the text. */
  onEdit: (row: SuboutTestPriceRow, field: 'tat' | 'price' | 'comment', value: string) => void;
}

export default function SuboutTestPricesTab({ rows, total, turnAroundTimes, readOnly, loading, onEdit }: Props) {
  const tatNames = useMemo(
    () => turnAroundTimes.map((t) => t.name ?? '').filter(Boolean), [turnAroundTimes]);

  const columns = useMemo<AppColumn<SuboutTestPriceRow>[]>(() => [
    { field: 'matrix', header: 'Matrix', width: 110, text: (r) => r.matrix ?? '' },
    { field: 'test',   header: 'Test',   width: 160, text: (r) => r.test ?? '' },
    { field: 'method', header: 'Method', width: 120, text: (r) => r.method ?? '' },
    { field: 'qty',    header: 'Qty',    width: 70, align: 'right', type: 'number', value: (r) => r.qty },
    {
      field: 'tat', header: 'TAT', width: 120, text: (r) => r.tat ?? '',
      edit: readOnly ? undefined : { type: 'combo', options: tatNames },
    },
    {
      field: 'unitPrice', header: 'UnitPrice', width: 110, align: 'right', type: 'number',
      value: (r) => r.unitPrice, text: (r) => money(r.unitPrice),
      edit: readOnly ? undefined : { type: 'number', display: (v) => money(Number(v)) },
    },
    {
      field: 'subTotal', header: 'SubTotal', width: 110, align: 'right', type: 'number',
      value: (r) => r.subTotal, text: (r) => money(r.subTotal),
      summary: (all) => `Total: ${money(all.reduce((s, r) => s + (r.subTotal ?? 0), 0))}`,
    },
    { field: 'contractLab', header: 'ContractLab', width: 150, text: (r) => r.contractLab ?? '' },
    {
      field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: 140, type: 'datetime',
      text: (r) => fmtLocalDateTime(r.lastUpdatedDate),
    },
    { field: 'changePercent', header: '%Change', width: 90, align: 'right', text: (r) => r.changePercent ?? '0' },
    { field: 'priceCode', header: 'PriceCode', width: 100, text: (r) => r.priceCode ?? '' },
    {
      field: 'comment', header: 'Comment', width: 200, text: (r) => r.comment ?? '',
      edit: readOnly ? undefined : { type: 'text' },
    },
  ], [readOnly, tatNames]);

  void total;   // the footer summary row shows it (G5 footer "Total")
  return (
      <AppDataGrid<SuboutTestPriceRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.testMethodOid}
        loading={loading}
        maximizable
        exportFilename="subout-test-prices"
        emptyText="No test prices."
        onCellCommit={(row, col, value) => {
          if (col.field === 'tat') {
            const t = turnAroundTimes.find((x) => x.name === value);
            onEdit(row, 'tat', t?.id ?? '');
          } else if (col.field === 'unitPrice') {
            onEdit(row, 'price', value);
          } else if (col.field === 'comment') {
            onEdit(row, 'comment', value);
          }
        }}
      />
  );
}
